import { Calendar } from "@components/ui/calendar";
import Popover from "@components/ui/popover";
import { cn } from "@lib/cn";
import { IconCalendarTime } from "@tabler/icons-react";
import moment from "moment";
import { useState } from "react";
import { fromDateValue, toDateValue } from "../control-utils";

interface Props {
  value: string;
  onChange: (value: string) => void;
  invalid?: boolean;
  /** moment format for the trigger label (e.g. "MMMM" for a month variable);
   * omitted → the raw `YYYY-MM-DD` value. */
  displayFormat?: string;
  /** Read-only: shows the value but never opens the calendar (built-in
   * system dates are filled for the sender, not chosen by them). */
  disabled?: boolean;
  /** Extra trigger classes, e.g. to style it like an input outside the
   * share dialog's borderless rows. */
  className?: string;
}

export const DateControl = ({
  value,
  onChange,
  invalid = false,
  displayFormat,
  disabled = false,
  className,
}: Props) => {
  const [open, setOpen] = useState(false);
  const selected = fromDateValue(value);
  const label =
    value && displayFormat && selected
      ? moment(selected).format(displayFormat)
      : value;

  return (
    <Popover
      open={open && !disabled}
      onOpenChange={(next) => setOpen(next && !disabled)}
      placement="bottom-start"
      offset={2}
    >
      <Popover.Trigger asChild>
        <button
          type="button"
          disabled={disabled}
          aria-disabled={disabled}
          className={cn(
            "tw-flex tw-h-9 tw-w-full tw-items-center tw-gap-2 tw-rounded-lg tw-border-none tw-bg-white tw-px-2 tw-text-sm tw-font-normal tw-text-blue-500 tw-outline-none",
            open && "tw-bg-[#f3f2f5]",
            value && "tw-text-blue",
            invalid && "tw-bg-[#FAF1F2] tw-text-[#D43548]",
            disabled && "tw-cursor-default tw-text-blue-700",
            className,
          )}
        >
          <IconCalendarTime className="tw-size-4 !tw-fill-none" />
          <span>{label || "Choose date"}</span>
        </button>
      </Popover.Trigger>
      <Popover.Content className="tw-z-[1000000]">
        <Calendar
          mode="single"
          selected={selected}
          defaultMonth={selected ?? new Date()}
          onSelect={(date) => {
            if (!date) return;
            onChange(toDateValue(date));
            setOpen(false);
          }}
        />
      </Popover.Content>
    </Popover>
  );
};
